import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AdScheduleCalendarPicker from './AdScheduleCalendarPicker';
import LocalText from '../common/LocalText';
import { getPlacementAvailabilityPerDay, updateAd } from '../../api/advertiserApi';
import { submitApproval } from '../../api/approvalApi';
import { useToast } from '../../context/ToastContext';
import { useApproval } from '../../context/ApprovalContext';
import { CONTENT_TYPES } from '../../constants/contentTypes';
import { formatDateRanges, splitRunDates, todayEastern } from '../../utils/dateUtils';

const C = { navy: '#0a326d', navyHover: '#0d3d85', line: '#dde6f0', text: '#1e3a5f', muted: '#64748b' };

/**
 * Shown instead of the "Submit for approval?" question when a draft ad still has run dates that have already passed.
 * Those days never ran (the ad was never approved), so they cannot go for approval as they are: the user picks the days
 * it should run from today on, and "Update ad and submit" saves the new days and sends the ad in one step.
 */
const AdPassedDatesDialog = ({ open, ad, campaign, placements = [], overrideSpaceId = null, adName = '', onClose, onDone }) => {
  const { toast } = useToast();
  const { addMySubmission } = useApproval();
  const [selected, setSelected] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const today  = todayEastern();
  const passed = (ad?.scheduleDates ?? []).map((d) => String(d).slice(0, 10)).filter((d) => d < today).sort();
  const ended  = Boolean(campaign?.endDate) && String(campaign.endDate).slice(0, 10) < today;
  const placementKey = ad?.placementKey ?? placements.find((p) => p.id === ad?.placementId)?.placementKey;
  const placementLabel = placements.find((p) => p.id === ad?.placementId)?.label ?? '';

  // Start from what the ad still has: its days from today on that sit inside the campaign.
  useEffect(() => {
    if (!open || !ad) return;
    const split = splitRunDates(ad.scheduleDates, campaign?.startDate, campaign?.endDate, todayEastern());
    setSelected(split.current.filter((d) => d >= todayEastern()));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ad?.id]);

  useEffect(() => {
    // Clear `loading` on this exit too: a cancelled request never reaches the .finally below.
    if (!open || !ad || !placementKey || !campaign?.startDate || !campaign?.endDate) { setAvailability([]); setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    getPlacementAvailabilityPerDay(campaign.startDate, campaign.endDate, ad.id, overrideSpaceId)
      .then((data) => { if (!cancelled) setAvailability(data.filter((d) => d.placementKey === placementKey)); })
      .catch(() => { if (!cancelled) setAvailability([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, ad?.id, placementKey, campaign?.startDate, campaign?.endDate, overrideSpaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateAndSubmit = async () => {
    setBusy(true);
    try {
      // Only the dates change; everything else on the ad is left as it is (null = unchanged).
      await updateAd(campaign.advertiserId, campaign.id, ad.id, { scheduleDates: selected });
      const result = await submitApproval({
        contentType: CONTENT_TYPES.AD,
        contentId: ad.id,
        contentName: adName,
        actionType: 'create',
      });
      addMySubmission(result?.approvalId);
      toast.success('Dates updated and submitted for approval!');
      onDone?.();
    } catch {
      // The global api-error handler shows the toast; the dialog stays open so the dates are not lost.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: C.navy, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
          <LocalText>Some run dates have already passed</LocalText>
        </Typography>
        <IconButton size="small" aria-label="close" onClick={onClose} disabled={busy} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.5, mb: 2, borderRadius: '10px', bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
          <WarningAmberIcon sx={{ fontSize: 18, color: '#d97706', mt: '1px' }} />
          <Typography sx={{ fontSize: 12.5, color: '#92400e' }} data-testid="passed-dates-summary">
            <LocalText>{`${adName || 'This ad'} can't be submitted yet. It was never approved, so it never ran on the days that have passed:`}</LocalText>
            <strong>{` ${formatDateRanges(passed, 4)}`}</strong>
            <LocalText>. Pick the days it should run, from today on.</LocalText>
          </Typography>
        </Box>

        {ended ? (
          <Alert severity="info" data-testid="passed-dates-ended" sx={{ fontSize: 12 }}>
            <LocalText>This campaign has ended, so no new run dates can be scheduled. Delete this ad, or create it in a campaign that is still running.</LocalText>
          </Alert>
        ) : (
          <>
            <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', mb: 0.5 }}>
              <LocalText>New run dates</LocalText>
            </Typography>
            <AdScheduleCalendarPicker
              campaignStartDate={campaign.startDate}
              campaignEndDate={campaign.endDate}
              adDurationSeconds={ad?.durationSeconds}
              availabilityData={availability}
              selectedDates={selected}
              onChange={setSelected}
              loading={loading}
              placementLabel={placementLabel}
              required
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50', gap: 1.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
          <LocalText>Cancel</LocalText>
        </Button>
        <Button
          variant="contained"
          disabled={ended || busy || selected.length === 0}
          onClick={handleUpdateAndSubmit}
          sx={{
            bgcolor: C.navy, color: 'white', fontWeight: 700, textTransform: 'none', borderRadius: '10px', px: 3,
            boxShadow: 'none', '&:hover': { bgcolor: C.navyHover, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: '#c8d8ea', color: 'white' },
          }}
        >
          {busy ? 'Submitting…' : <LocalText>Update ad and submit</LocalText>}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdPassedDatesDialog;
